import { Code, ConnectError } from '@connectrpc/connect';
import { registryHref } from '../../registryHref';
import { registryRecordClient } from '../registryRecordClient';
/**
 * The Person page's client over REG-10's RegistryRecordService RPCs
 * (GetContactDossier, ListContactTouchpointsUnified, ListEntityDocuments).
 * Every filter (visible programs and pursuits, view_sensitive masking,
 * area:growth touchpoints) is applied by the server; this only maps shapes.
 */ const toDate = (t)=>{
    if (!t) return null;
    const d = t.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
};
const ref = (r)=>r?.kind && r.id ? {
        kind: r.kind,
        id: r.id,
        name: r.name
    } : undefined;
const PLANES = [
    'GROWTH',
    'PROPOSAL',
    'DELIVERY'
];
const SECTORS = [
    'GOVERNMENT',
    'INDUSTRY',
    'OURS'
];
const BASES = [
    'stated',
    'observed',
    'inferred',
    'unknown'
];
function card(c) {
    const primary = ref(c.primary);
    if (!primary || !PLANES.includes(c.plane)) return null;
    const contexts = c.contexts.map(ref).filter((x)=>!!x);
    return {
        plane: c.plane,
        title: c.title,
        summary: c.summary,
        visibleCount: c.visibleCount,
        primary,
        contexts: contexts.length > 0 ? contexts : [
            primary
        ]
    };
}
function span(e, index) {
    return {
        id: e.employmentId || `form-${index}`,
        title: e.title,
        employer: ref(e.employer),
        employerText: e.employer?.name || e.employerText,
        sector: SECTORS.includes(e.sector) ? e.sector : '',
        startDate: toDate(e.startDate),
        endDate: toDate(e.endDate),
        observedFrom: toDate(e.observedFrom),
        observedTo: toDate(e.observedTo),
        dateBasis: BASES.includes(e.dateBasis) ? e.dateBasis : 'unknown',
        sourceText: e.source?.text || 'Imported',
        fromContactForm: e.fromContactForm
    };
}
const isNotFound = (err)=>err instanceof ConnectError && (err.code === Code.NotFound || err.code === Code.PermissionDenied);
const HOME_LABEL = {
    LIBRARY: 'Library',
    PRICING_SHEET: 'Pricing sheet'
};
export const contactDossierClient = {
    async getContactDossier (_tenantId, contactId) {
        let resp;
        try {
            resp = await registryRecordClient.getContactDossier({
                contactId
            });
        } catch (err) {
            if (isNotFound(err)) return null;
            throw err;
        }
        const current = resp.current[0];
        return {
            id: resp.contactId,
            name: resp.name || 'Unnamed contact',
            currentTitle: current?.title ?? '',
            employer: ref(current?.employer),
            employerText: current?.employer?.name || current?.employerText || '',
            hasPlatformAccount: resp.hasPlatformAccount,
            isActive: resp.isActive,
            requestedIdWasMerged: resp.requestedIdWasMerged,
            appearances: resp.appearances.map(card).filter((c)=>!!c),
            career: resp.career.map(span),
            whoKnows: resp.whoKnows.map((w, i)=>({
                    id: w.internalContactId || w.userId || `who-${i}`,
                    name: w.name,
                    meetingCount: w.meetingCount,
                    lastMetAt: toDate(w.lastMetAt),
                    contextHint: w.contextHint
                })),
            interests: resp.interests.map((x, i)=>({
                    id: `interest-${i}`,
                    statement: x.statement,
                    sourceText: x.source?.text ?? ''
                })),
            visibleTouchpointCount: resp.visibleTouchpointCount
        };
    },
    async listContactTouchpointsUnified (_tenantId, contactId, afterId) {
        const resp = await registryRecordClient.listContactTouchpointsUnified({
            contactId,
            afterId,
            limit: 20
        });
        return {
            items: resp.touchpoints.flatMap((t)=>{
                const context = ref(t.context);
                if (!context) return [];
                return [
                    {
                        id: t.id,
                        at: toDate(t.at),
                        summary: t.summary,
                        masked: t.masked,
                        context
                    }
                ];
            }),
            visibleTotal: resp.visibleTotal,
            nextAfterId: resp.nextAfterId
        };
    },
    async listEntityDocuments (_tenantId, contactId) {
        const resp = await registryRecordClient.listEntityDocuments({
            entityKind: 'CONTACT',
            entityId: contactId
        });
        return {
            documents: resp.documents.map((d)=>{
                const home = ref(d.homeRef);
                return {
                    id: `${d.home}:${home?.id ?? ''}:${d.artifactId}`,
                    filename: d.title,
                    href: home ? registryHref(home) : null,
                    homeLabel: home?.kind === 'PROGRAM' ? home.name : HOME_LABEL[d.home] ?? 'Library'
                };
            }),
            canLink: resp.canLink
        };
    }
};
